import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import Topbar from '../../components/common/Topbar';
import Toast from '../../components/common/Toast';
import { showToast } from '../../store/uiSlice';
import { updateUser } from '../../store/authSlice';
import api from '../../services/api';

const Profile = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(true);
  const [formData, setFormData] = useState({
    firstName: '', lastName: '', email: '', phone: '',
    dateOfBirth: '', address: '', city: '', country: '',
  });

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await api.get('/user/profile');
        const profileData = response.data;
        dispatch(updateUser(profileData));
        setFormData({
          firstName: profileData.firstName || '', lastName: profileData.lastName || '',
          email: profileData.email || '', phone: profileData.phone || '',
          dateOfBirth: profileData.dateOfBirth || '', address: profileData.address || '',
          city: profileData.city || '', country: profileData.country || '',
        });
      } catch (error) {
        if (user) {
          setFormData({
            firstName: user.firstName || '', lastName: user.lastName || '',
            email: user.email || '', phone: user.phone || '',
            dateOfBirth: user.dateOfBirth || '', address: user.address || '',
            city: user.city || '', country: user.country || '',
          });
        }
      } finally { setIsFetching(false); }
    };
    fetchProfile();
  }, []);

  const handleChange = (e) => { setFormData({ ...formData, [e.target.name]: e.target.value }); };

  const handleSave = async () => {
    setIsLoading(true);
    try {
      const response = await api.put('/user/profile', formData);
      dispatch(updateUser(response.data.user));
      dispatch(showToast({ message: 'Profile updated successfully!', type: 'success' }));
      setIsEditing(false);
    } catch (error) {
      dispatch(showToast({ message: error.response?.data?.message || 'Failed to update profile', type: 'error' }));
    } finally { setIsLoading(false); }
  };

  const formatDateForInput = (dateStr) => {
    if (!dateStr) return '';
    try { const date = new Date(dateStr); if (isNaN(date.getTime())) return ''; return date.toISOString().split('T')[0]; }
    catch { return ''; }
  };

  const formatDateForDisplay = (dateStr) => {
    if (!dateStr) return 'Not set';
    try { const date = new Date(dateStr); if (isNaN(date.getTime())) return 'Not set'; return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }); }
    catch { return 'Not set'; }
  };

  if (isFetching) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--color-bg)' }}>
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-2 rounded-full animate-spin" style={{ borderColor: 'var(--color-orange)', borderTopColor: 'transparent' }} />
          <p className="font-mono text-sm" style={{ color: 'var(--color-text)' }}>Loading profile…</p>
        </div>
      </div>
    );
  }

  const inputStyle = {
    background: 'var(--color-bgInput)', border: '1px solid var(--color-borderMedium)',
    color: 'var(--color-text)', outline: 'none', transition: 'all 0.2s',
  };

  const rowStyle = { borderBottom: '1px solid var(--color-borderLight)' };
  const cardStyle = { background: 'var(--color-bgSurface)', border: '1px solid var(--color-border)' };

  return (
    <div className="min-h-screen" style={{ background: 'var(--color-bg)' }}>
      <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
        <div className="absolute w-[700px] h-[700px] rounded-full top-[-260px] right-[-200px] animate-float"
          style={{ background: 'radial-gradient(circle, rgba(46,54,66,0.08), transparent 65%)' }} />
        <div className="absolute w-[600px] h-[600px] rounded-full bottom-[-200px] left-[-160px] animate-float animation-delay-2000"
          style={{ background: 'radial-gradient(circle, rgba(139,92,246,0.05), transparent 65%)' }} />
      </div>

      <Topbar />

      <div className="relative z-10 pt-[52px] p-5 flex items-center justify-center min-h-[calc(100vh-52px)]">
        <div className="w-full max-w-[800px]">
          

          <div className="text-center mb-8 animate-slide-up">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-3"
              style={{ background: 'var(--color-orangeBg)', border: '1px solid var(--color-orangeBorder)' }}>
              <i className="fas fa-user-circle text-[9px]" style={{ color: 'var(--color-text)' }} />
              <span className="text-[9px] font-mono tracking-wide" style={{ color: 'var(--color-text)' }}>ACCOUNT</span>
            </div>
            <h1 className="font-syne text-3xl md:text-4xl font-extrabold mb-2" style={{ color: 'var(--color-text)' }}>
              Personal <span style={{ color: 'var(--color-text)' }}>Details</span>
            </h1>
            <p className="text-sm" style={{ color: 'var(--color-textSecondary)' }}>View and manage your profile information</p>
          </div>

          <div className="rounded-2xl overflow-hidden animate-slide-up animation-delay-100" style={cardStyle}>
            <div className="flex items-center gap-3 px-6 py-4 border-b" style={{ background: 'var(--color-bgCard)', borderColor: 'var(--color-borderMedium)' }}>
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'var(--color-orangeBg)', border: '1px solid var(--color-orangeBorder)', color: 'var(--color-text)' }}>
                <i className="fas fa-user-circle text-base" />
              </div>
              <div>
                <div className="font-syne text-base font-extrabold" style={{ color: 'var(--color-text)' }}>Personal Information</div>
                <div className="text-[9px] font-mono" style={{ color: 'var(--color-textDim)' }}>Your account details</div>
              </div>
            </div>

            <div className="p-6">
              {!isEditing ? (
                <div className="space-y-4">
                  {[
                    { label: 'Full Name', value: `${formData.firstName} ${formData.lastName}` },
                    { label: 'Email Address', value: formData.email },
                    { label: 'Phone Number', value: formData.phone || 'Not set' },
                    { label: 'Date of Birth', value: formatDateForDisplay(formData.dateOfBirth) },
                    { label: 'Address', value: formData.address || 'Not set' },
                    { label: 'City', value: formData.city || 'Not set' },
                    { label: 'Country', value: formData.country || 'Not set' },
                  ].map((row, i) => (
                    <div key={row.label} className="flex justify-between items-center py-3" style={i < 6 ? rowStyle : {}}>
                      <div className="text-[9px] font-mono tracking-wide uppercase" style={{ color: 'var(--color-textDim)' }}>{row.label}</div>
                      <div className="font-syne text-sm font-bold" style={{ color: 'var(--color-text)' }}>{row.value}</div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    {['firstName', 'lastName'].map(name => (
                      <div key={name}>
                        <label className="text-[9px] font-mono tracking-wide uppercase block mb-2" style={{ color: 'var(--color-text)' }}>{name === 'firstName' ? 'First Name' : 'Last Name'}</label>
                        <input type="text" name={name} value={formData[name]} onChange={handleChange}
                          className="w-full rounded-lg px-3 py-2 font-dm text-sm" style={inputStyle}
                          onFocus={e => e.target.style.borderColor = 'var(--color-orange)'}
                          onBlur={e => e.target.style.borderColor = 'var(--color-borderMedium)'} />
                      </div>
                    ))}
                  </div>
                  {[
                    { name: 'email', label: 'Email Address', disabled: true },
                    { name: 'phone', label: 'Phone Number', type: 'tel' },
                    { name: 'dateOfBirth', label: 'Date of Birth', type: 'date' },
                    { name: 'address', label: 'Address' },
                    { name: 'city', label: 'City' },
                    { name: 'country', label: 'Country' },
                  ].map(field => (
                    <div key={field.name}>
                      <label className="text-[9px] font-mono tracking-wide uppercase block mb-2" style={{ color: 'var(--color-text)' }}>{field.label}</label>
                      <input type={field.type || 'text'} name={field.name}
                        value={field.type === 'date' ? formatDateForInput(formData[field.name]) : formData[field.name]}
                        onChange={handleChange} disabled={field.disabled}
                        className={`w-full rounded-lg px-3 py-2 font-dm text-sm outline-none transition-all ${field.disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
                        style={{ ...inputStyle, color: field.disabled ? 'var(--color-textSecondary)' : 'var(--color-text)' }}
                        onFocus={e => { if (!field.disabled) e.target.style.borderColor = 'var(--color-orange)'; }}
                        onBlur={e => { if (!field.disabled) e.target.style.borderColor = 'var(--color-borderMedium)'; }} />
                      {field.disabled && <p className="text-[7px] font-mono mt-1" style={{ color: 'var(--color-textDim)' }}>Email cannot be changed</p>}
                    </div>
                  ))}
                </div>
              )}

              <div className="mt-8">
                {!isEditing ? (
                  <button onClick={() => setIsEditing(true)} className="w-full flex items-center justify-center gap-2 py-3 rounded-full font-syne text-sm font-bold transition-all hover:-translate-y-0.5"
                    style={{ background: 'var(--color-orangeBg)', border: '1px solid var(--color-orangeBorder)', color: 'var(--color-text)' }}>
                    <i className="fas fa-pen text-xs" /> Edit Profile
                  </button>
                ) : (
                  <div className="flex gap-3">
                    <button onClick={() => setIsEditing(false)} className="flex-1 py-3 rounded-full font-syne text-sm font-bold transition-all"
                      style={{ background: 'var(--color-bgCard)', border: '1px solid var(--color-borderHover)', color: 'var(--color-textMuted)' }}>Cancel</button>
                    <button onClick={handleSave} disabled={isLoading} className="flex-1 py-3 rounded-full font-syne text-sm font-bold transition-all active:translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed"
                      style={{ background: 'var(--color-gradientOrange)', color: 'var(--color-textInverse)' }}>
                      {isLoading ? <><span className="inline-block w-3 h-3 border-2 rounded-full animate-spin mr-1" style={{ borderColor: 'var(--color-textInverse)', borderTopColor: 'transparent' }} /> Saving</> : <><i className="fas fa-save mr-1" /> Save Changes</>}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <Toast />
      <style>{`
        @keyframes float { 0%, 100% { transform: translate(0, 0); } 50% { transform: translate(28px, -28px); } }
        @keyframes slide-up { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        .animate-float { animation: float 20s ease-in-out infinite; }
        .animation-delay-2000 { animation-delay: -8s; }
        .animate-slide-up { animation: slide-up 0.6s ease-out both; }
        .animation-delay-100 { animation-delay: 0.1s; }
      `}</style>
    </div>
  );
};

export default Profile;